'use client';

import React from 'react';
import Image from 'next/image';
import Profile from '@/assets/icons/common/avatar.svg';
import { domain_url } from '@/constants/apiConstant';
import NewsLayouts from '@/layouts/Newslayouts';
import RootLayouts from '@/layouts/Rootlayouts';
import { newsBlogFormatDate } from '@/utils/formatDate';
import sanitizeHTML from '@/utils/sanatizeHtml';
import { generateBreadcrumbSchema } from '@/utils/seo/schema';
import * as cheerio from 'cheerio';

import Sidebar from '@/components/blogs/Sidebar';
import Breadcrumb from '@/components/common/seo/BreadCrumbs';
import Separator from '@/components/common/Separator';
import NewsSidebar from '@/components/news/NewsRightSidebar';
import RelatedNewsCard from '@/components/news/RelatedNewsCard';

const processNewsContent = (html: string, featuredImage?: { url: string; name?: string }) => {
  const $ = cheerio.load(html);

  // Style all links in the content to be blue
  $('a').css({
    color: '#2563eb', // Blue color (Tailwind's blue-600)
    'text-decoration': 'underline',
    cursor: 'pointer',
  });

  // Add hover effect using classes
  $('a').addClass('hover:text-blue-800');

  // Insert a properly-sized image at the beginning of the content with better styling - positioned on LEFT side
  if (featuredImage?.url) {
    // Find the first paragraph or element to insert before
    const firstElement = $('p, h1, h2, h3, h4, h5, h6').first();

    if (firstElement.length) {
      firstElement.before(`
        <div style="float: left; margin-right: 24px; margin-bottom: 20px; max-width: 450px; width: 100%; margin-top: 5px;">
          <img 
            src="${featuredImage.url}" 
            alt="${featuredImage.name ?? 'News Featured Image'}"
            width="450" 
            height="300"
            style="border-radius: 8px; width: 100%; height: auto; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);" 
          />
        </div>
      `);
    }
  }

  return $('body').html() ?? html;
};

const NewsLandingPage = ({ newsData, newsId,relatedNews }: any) => {
  const news_url = newsId && newsData && `${domain_url}/news/${newsData?.url}`;
  const title = newsData?.name;

  const breadcrumbItems = generateBreadcrumbSchema(title, news_url, domain_url, 'news', 'News');

  const cleanedHTML = sanitizeHTML(newsData?.content ?? '');
  const finalHTML = processNewsContent(cleanedHTML, newsData?.featured_image);
  
  return (
    <RootLayouts>
      <div className="mx-auto mb-4 mt-24 w-full  lg:max-w-[73.75rem] 2xl:max-w-screen-xl ">
        <Breadcrumb items={breadcrumbItems} color="text-black" />
      </div>
      <NewsLayouts>
        <main>
          <section className="mx-auto mt-3 max-w-screen-xl p-2 lg:mt-5">
            <div className="flex flex-col gap-8 md:flex-row">
              <div className="flex h-full flex-1 flex-col gap-7 overflow-y-auto">
                <div className="hide-scrollbar flex h-full flex-col gap-1">
                  <div className="space-y-1">
                    <h1 className="mb-3 font-times text-2xl font-medium leading-6 tracking-tight">{title}</h1>
                    <span className="flex items-center gap-x-2 text-xs">{newsBlogFormatDate(newsData?.updatedAt)}</span>
                    <div className="flex-col items-start gap-2 text-sm text-black sm:gap-x-4">
                      <div className="flex w-auto flex-col gap-1">
                        <div className="flex items-center gap-2 ">
                          <p className="text-xs text-gray-600">
                            {newsData?.updated_by_user ? 'Updated By -' : 'Created By -'}
                          </p>
                          <Image
                            src={
                              newsData?.updated_by_user?.profile_picture?.url ??
                              newsData?.created_by_user?.profile_picture?.url ??
                              Profile
                            }
                            alt={
                              newsData?.updated_by_user?.name ??
                              newsData?.created_by_user?.name ??
                              newsData?.author_name ??
                              'Tanya Verma'
                            }
                            width={20}
                            height={20}
                            className="size-5 rounded-full"
                          />
                          <span className="truncate whitespace-nowrap text-xs text-gray-800">
                            {newsData?.updated_by_user?.name ??
                              newsData?.created_by_user?.name ??
                              newsData?.author_name ??
                              'Tanya Verma'}
                          </span>
                        </div>

                        {/* Designation Section */}
                        {(newsData?.updated_by_user?.designation ?? newsData?.created_by_user?.designation) && (
                          <p className="line-clamp-1 text-xs text-gray-500">
                            <span className="font-medium text-gray-700">Designation - </span>
                            {newsData?.updated_by_user?.designation ?? newsData?.created_by_user?.designation}
                          </p>
                        )}

                        {/* Author Bio Section */}
                        {(newsData?.updated_by_user?.author_bio ?? newsData?.created_by_user?.author_bio) && (
                          <p className="text-xs text-gray-500">
                            <span className="font-medium text-gray-700">Author Bio - </span>
                            {newsData?.updated_by_user?.author_bio ?? newsData?.created_by_user?.author_bio}
                          </p>
                        )}
                      </div>

                      {/* Tags Section */}
                      {newsData?.tags?.length > 0 && (
                        <>
                          <div className="hidden h-5 w-[0.8px] bg-gray-400"></div>
                          <div className="mt-2 flex items-center gap-2">
                            <p className="text-gray-600 ">Tags:</p>
                            <div className="flex gap-2">
                              {newsData?.tags?.map((tag: any) => (
                                <p
                                  key={tag?.id}
                                  className="rounded-full bg-tertiary px-4 py-1 text-xs font-medium text-white"
                                >
                                  {tag?.tags_name}
                                </p>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div
                    dangerouslySetInnerHTML={{ __html: finalHTML }}
                    className={`ckeditorData mt-2 w-full min-w-fit `}
                  />
                </div>

                <Sidebar title={title} url={news_url} />
              </div>
              <div className="sticky top-24 h-fit">
                <NewsSidebar title="Follow us" />
              </div>
            </div>
            <div className="my-8">
              <Separator />
            </div>

            {relatedNews?.length > 0 && (
              <div className="flex flex-col gap-4">
                <h3 className="font-times text-2xl font-medium underline decoration-1 underline-offset-4">
                  Related News
                </h3>
                <div className="md:grid-col-4 grid-col-1 mb-2 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-6 xl:grid-cols-5">
                  {relatedNews?.slice(0, 5)?.map((latestData: any) => {
                    return <RelatedNewsCard key={latestData.id} latestData={latestData} />;
                  })}
                </div>
              </div>
            )}
          </section>
        </main>
      </NewsLayouts>
    </RootLayouts>
  );
};

export default NewsLandingPage;
